// "Hartă": the documents that carry a location (requests, petitions, urbanism dossiers…), as clusters, points or a heat map.
// Only the documents the user may open are on the map (same rights as the lists).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, qs } from '../api';
import { useCatalog } from '../catalog';
import { navigate, useRoute } from '../store';
import { DatePicker, EmptyState, fmtDate, useToast } from '../ui';
import { Icon } from '../ui/Icon';
import { MapBox } from '../maps/components';
import { DocumentDrawer } from './DocumentDrawer';

interface Feature { type: 'Feature'; geometry: { type: 'Point'; coordinates: [number, number] }; properties: Record<string, any> }
interface FC { type: 'FeatureCollection'; features: Feature[]; truncated?: boolean }

const PALETTE = ['#4f6ef7', '#e8590c', '#2f9e44', '#ae3ec9', '#1098ad', '#f08c00', '#d6336c', '#5c940d', '#495057', '#7048e8', '#0b7285', '#c92a2a'];
const STATUS: Record<string, { label: string; color: string }> = {
  RECEIVED: { label: 'Primit', color: '#0ea5e9' }, IN_WORK: { label: 'În lucru', color: '#f59e0b' }, TO_DISTRIBUTE: { label: 'Spre distribuire', color: '#8b5cf6' },
  FINAL_APPROVAL: { label: 'Aprobare finală', color: '#d6336c' }, RESOLVED: { label: 'Rezolvat', color: '#22c55e' },
};
type ColorBy = 'type' | 'status' | 'tip';

export function MapPage() {
  const route = useRoute();
  const toast = useToast();
  const { types } = useCatalog();
  // the types that have documents with a location (remembered from the unfiltered answers), plus the registers
  const [withLoc, setWithLoc] = useState<Record<number, number>>({});
  const [sel, setSel] = useState<number[]>([]);
  const registers = useMemo(() => types.filter(t => t.active !== false && (withLoc[t.id] || (t.isRegistry && t.kind !== 'GENERIC') || sel.includes(t.id))), [types, withLoc, sel]);
  const [state, setState] = useState(route.params.get('state') || '');
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [qq, setQq] = useState('');
  const [mode, setMode] = useState<'points' | 'heat'>('points');
  const [colorBy, setColorBy] = useState<ColorBy>('type');
  const [data, setData] = useState<FC | null>(null);
  const [openId, setOpenId] = useState<number | null>(route.params.get('doc') ? Number(route.params.get('doc')) : null);
  const mapRef = useRef<any>(null), libRef = useRef<any>(null), popupRef = useRef<any>(null);
  const focusDoc = useRef<number | null>(route.params.get('doc') ? Number(route.params.get('doc')) : null);

  useEffect(() => { const t = setTimeout(() => setQq(q), 300); return () => clearTimeout(t); }, [q]);
  const load = () => api.get<FC>('/api/documents/map' + qs({ types: sel.join(','), state, from: from || undefined, to: to || undefined, q: qq || undefined }))
    .then(d => {
      setData(d);
      if (!sel.length) { const c: Record<number, number> = {}; for (const f of d.features) c[f.properties.typeId] = (c[f.properties.typeId] || 0) + 1; setWithLoc(c); }
    }).catch(e => toast(e.message, 'error'));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sel.join(','), state, from, to, qq]);

  // colours: by register / type, by status, or by the request type ("Tip document", procedure)
  const legend = useMemo(() => {
    const out = new Map<string, { label: string; color: string; n: number }>();
    for (const f of data?.features || []) {
      const p = f.properties;
      const key = colorBy === 'status' ? p.status : colorBy === 'tip' ? (p.tip || '—') : String(p.typeId);
      const label = colorBy === 'status' ? (STATUS[p.status]?.label || p.status) : colorBy === 'tip' ? (p.tip || 'nespecificat') : p.typeName;
      const e = out.get(key);
      if (e) e.n++; else out.set(key, { label, color: '', n: 1 });
    }
    let i = 0;
    for (const [k, v] of out) v.color = colorBy === 'status' ? (STATUS[k]?.color || '#868e96') : PALETTE[i++ % PALETTE.length];
    return out;
  }, [data, colorBy]);

  const geo = useMemo<FC>(() => ({
    type: 'FeatureCollection',
    features: (data?.features || []).map(f => {
      const p = f.properties;
      const key = colorBy === 'status' ? p.status : colorBy === 'tip' ? (p.tip || '—') : String(p.typeId);
      return { ...f, properties: { ...p, color: legend.get(key)?.color || '#868e96' } };
    }),
  }), [data, legend, colorBy]);
  const geoRef = useRef(geo); geoRef.current = geo;

  // push the data into the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('docs');
    if (src) src.setData(geo);
    if (focusDoc.current != null) {
      const f = geo.features.find(x => x.properties.id === focusDoc.current);
      if (f) { map.flyTo({ center: f.geometry.coordinates, zoom: 17, duration: 0 }); focusDoc.current = null; }
    }
  }, [geo]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('docs-heat')) return;
    const heat = mode === 'heat';
    map.setLayoutProperty('docs-heat', 'visibility', heat ? 'visible' : 'none');
    for (const l of ['clusters', 'cluster-count', 'docs-point', 'docs-overdue']) map.setLayoutProperty(l, 'visibility', heat ? 'none' : 'visible');
  }, [mode]);

  const onReady = (map: any, _cfg: any, lib: any) => {
    mapRef.current = map; libRef.current = lib;
    map.addSource('docs', { type: 'geojson', data: geoRef.current, cluster: true, clusterMaxZoom: 15, clusterRadius: 44 });
    map.addLayer({ id: 'docs-heat', type: 'heatmap', source: 'docs', layout: { visibility: 'none' }, paint: {
      'heatmap-weight': ['case', ['has', 'point_count'], ['min', 1, ['/', ['get', 'point_count'], 10]], 0.4],
      'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 15, 2.2], 'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 8, 14, 16, 34], 'heatmap-opacity': 0.8 } });
    map.addLayer({ id: 'clusters', type: 'circle', source: 'docs', filter: ['has', 'point_count'], paint: {
      'circle-color': ['step', ['get', 'point_count'], '#74c0fc', 10, '#4f6ef7', 50, '#3b5bdb', 200, '#364fc7'],
      'circle-radius': ['step', ['get', 'point_count'], 15, 10, 19, 50, 24, 200, 30], 'circle-stroke-width': 3, 'circle-stroke-color': 'rgba(255,255,255,.85)' } });
    map.addLayer({ id: 'cluster-count', type: 'symbol', source: 'docs', filter: ['has', 'point_count'],
      layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Medium'], 'text-size': 13 }, paint: { 'text-color': '#fff' } });
    map.addLayer({ id: 'docs-overdue', type: 'circle', source: 'docs', filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'overdue'], true]],
      paint: { 'circle-radius': 11, 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': '#e03131', 'circle-stroke-width': 2.5 } });
    map.addLayer({ id: 'docs-point', type: 'circle', source: 'docs', filter: ['!', ['has', 'point_count']],
      paint: { 'circle-radius': 7, 'circle-color': ['get', 'color'], 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });
    map.on('click', 'clusters', async (e: any) => {
      const f = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0];
      const z = await map.getSource('docs').getClusterExpansionZoom(f.properties.cluster_id);
      map.easeTo({ center: f.geometry.coordinates, zoom: z });
    });
    map.on('click', 'docs-point', (e: any) => {
      const p = e.features[0].properties;
      const el = document.createElement('div');
      el.className = 'dfmap-pop';
      el.innerHTML = `<b>${esc(p.regFull || '')} ${esc(p.title || '')}</b><div class="muted">${esc(p.typeName || '')} · ${esc(STATUS[p.status]?.label || p.status)}${p.deadline ? ' · termen ' + fmtDate(p.deadline) : ''}${p.overdue === true || p.overdue === 'true' ? ' <span style="color:#e03131">depășit</span>' : ''}</div>` +
        (p.address ? `<div>${esc(p.address)}</div>` : '') + `<div style="margin-top:6px"><a data-open="${p.id}">Deschide documentul</a></div>`;
      el.querySelector('a')!.addEventListener('click', () => { popupRef.current?.remove(); setOpenId(Number(p.id)); });
      popupRef.current?.remove();
      popupRef.current = new lib.maplibregl.Popup({ offset: 12, maxWidth: '320px' }).setLngLat(e.features[0].geometry.coordinates).setDOMContent(el).addTo(map);
    });
    for (const l of ['clusters', 'docs-point']) { map.on('mouseenter', l, () => { map.getCanvas().style.cursor = 'pointer'; }); map.on('mouseleave', l, () => { map.getCanvas().style.cursor = ''; }); }
    if (focusDoc.current != null) {
      const f = geoRef.current.features.find(x => x.properties.id === focusDoc.current);
      if (f) { map.jumpTo({ center: f.geometry.coordinates, zoom: 17 }); focusDoc.current = null; }
    }
    return () => { mapRef.current = null; };
  };

  const total = data?.features.length ?? 0;
  const fit = () => {
    const map = mapRef.current, lib = libRef.current;
    if (!map || !geo.features.length) return;
    const b = new lib.maplibregl.LngLatBounds();
    for (const f of geo.features) b.extend(f.geometry.coordinates);
    map.fitBounds(b, { padding: 60, maxZoom: 16 });
  };

  return (
    <div className="map-page">
      <aside className="map-filters">
        <div className="section-title" style={{ marginTop: 0 }}>Ce se vede pe hartă</div>
        <input className="input" placeholder="Caută număr, nume, adresă…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="map-filter-group">
          <label className="muted small">Registre / tipuri de documente</label>
          {registers.map(t => (
            <label key={t.id} className="checkbox"><input type="checkbox" checked={sel.includes(t.id)} onChange={e => setSel(s => e.target.checked ? [...s, t.id] : s.filter(x => x !== t.id))} /><span className="grow">{t.name}</span>{withLoc[t.id] ? <span className="muted small">{withLoc[t.id]}</span> : null}</label>
          ))}
          {sel.length > 0 && <button className="btn ghost sm" onClick={() => setSel([])}>Toate</button>}
        </div>
        <div className="map-filter-group">
          <label className="muted small">Stare</label>
          <select className="select" value={state} onChange={e => setState(e.target.value)}>
            <option value="">Toate</option><option value="open">Nerezolvate</option><option value="overdue">Cu termenul depășit</option><option value="resolved">Rezolvate</option>
          </select>
        </div>
        <div className="map-filter-group">
          <label className="muted small">Înregistrate între</label>
          <div className="row" style={{ gap: 6 }}><DatePicker value={from} onChange={setFrom} placeholder="de la" /><DatePicker value={to} onChange={setTo} placeholder="până la" /></div>
        </div>
        <div className="map-filter-group">
          <label className="muted small">Afișare</label>
          <div className="seg"><button className={mode === 'points' ? 'active' : ''} onClick={() => setMode('points')}>Puncte</button><button className={mode === 'heat' ? 'active' : ''} onClick={() => setMode('heat')}>Densitate</button></div>
          <label className="muted small" style={{ marginTop: 8 }}>Culori după</label>
          <select className="select" value={colorBy} onChange={e => setColorBy(e.target.value as ColorBy)}>
            <option value="type">Registru / tip de document</option><option value="status">Stare</option><option value="tip">Tipul cererii</option>
          </select>
        </div>
        <div className="map-legend-list">
          {[...legend.values()].sort((a, b) => b.n - a.n).map(l => <div key={l.label} className="row small" style={{ gap: 6 }}><i className="legend-dot" style={{ background: l.color }} /><span className="grow">{l.label}</span><b>{l.n}</b></div>)}
          {total > 0 && <div className="row small muted" style={{ gap: 6 }}><i className="legend-dot ring" /><span>termen depășit</span></div>}
        </div>
        <div className="muted small">{total} {total === 1 ? 'document' : 'documente'} cu locație{data?.truncated ? ' (primele 5 000)' : ''}. Pe hartă apar doar documentele pe care le puteți deschide.</div>
        <button className="btn sm" onClick={fit} disabled={!total}><Icon name="maximize" />Încadrează toate</button>
      </aside>
      <div className="map-main">
        {data && total === 0 && <div className="map-empty"><EmptyState icon="pin" title="Niciun document cu locație" text="Documentele primesc o locație din câmpul „Amplasament” al registrului sau din portal, când cetățeanul alege punctul pe hartă." /></div>}
        <MapBox height="100%" onReady={onReady} />
      </div>
      {openId != null && <DocumentDrawer id={openId} onClose={() => { setOpenId(null); navigate('/map'); }} onChanged={load} />}
    </div>
  );
}
const esc = (s: string) => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
